/**
 * Shared ContentItems list view (issue #173). Used by both the Content
 * tab on `SiteDetailPage` and the standalone `ContentItemsListPage`
 * route at `/sites/$siteId/content`.
 *
 * Renders a paginated, type/blockType-filterable table of active
 * ContentItems for a site, plus a "New content item" dialog and per-row
 * Edit / Soft-delete actions.
 *
 * Filtering: the API today only supports `page` / `pageSize` query
 * params (`listContentItemsQuerySchema`), so the type/blockType filters
 * are applied client-side against the currently-fetched page. This is
 * acceptable for slice-1 sites which carry a small number of items;
 * a future API tweak can move the filter to the server without
 * changing the component contract.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Link } from "@tanstack/react-router";
import type { BlockType, ContentItemResponse, ContentItemType } from "@ottopilot/shared";
import { useContentItemsForSite, useDeleteContentItem } from "../../hooks/useContentItems";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { useToast } from "../../components/ToastProvider";
import { CreateContentItemDialog } from "./CreateContentItemDialog";

const ALL_CONTENT_ITEM_TYPES: ContentItemType[] = [
  "PAGE_BLOCK",
  "BLOG_POST",
  "PRODUCT",
  "FAQ",
  "CTA",
  "IMAGE",
  "SEO",
  "CUSTOM",
];

const ALL_BLOCK_TYPES: BlockType[] = [
  "HERO",
  "TEXT",
  "FEATURE_GRID",
  "BENEFITS",
  "PROBLEM_SOLUTION",
  "CTA",
  "FAQ",
  "TESTIMONIALS",
  "PRODUCT_GRID",
  "BLOG_CONTENT",
  "BLOG_LIST",
  "CONTACT_FORM",
  "IMAGE",
  "COMPARISON_TABLE",
  "PRICING_TABLE",
  "TRUST_BADGES",
  "NEWSLETTER",
];

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

type Props = {
  siteId: string;
};

/**
 * The table itself, with filter bar + pagination. Lifted into its own
 * component so the route page and the tab can share the same body
 * without duplicating the filter / pagination state.
 */
export function ContentItemsTable({ siteId }: Props): ReactElement {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [typeFilter, setTypeFilter] = useState<ContentItemType | "">("");
  const [blockTypeFilter, setBlockTypeFilter] = useState<BlockType | "">("");
  const [createOpen, setCreateOpen] = useState(false);

  const items = useContentItemsForSite(siteId, { page, pageSize });
  const remove = useDeleteContentItem(siteId);
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  /**
   * Client-side filter: only rows that match the (optional) type +
   * blockType filters survive. Empty string = no filter. Memoised so
   * the table doesn't re-filter every keystroke / pagination flip.
   */
  const visibleRows: ContentItemResponse[] = useMemo(() => {
    const rows = items.data?.data ?? [];
    return rows.filter((row) => {
      if (typeFilter !== "" && row.type !== typeFilter) return false;
      if (blockTypeFilter !== "" && row.blockType !== blockTypeFilter) return false;
      return true;
    });
  }, [items.data, typeFilter, blockTypeFilter]);

  const handleDelete = async (row: ContentItemResponse) => {
    const ok = await confirm({
      title: `Soft-delete "${row.name}"?`,
      body: `Soft-delete content item "${row.name}"? It will be hidden from this list, and pages referencing it will render a placeholder. Its versions are kept for audit.`,
      confirmLabel: "Soft-delete",
    });
    if (!ok) return;
    remove.mutate(
      { id: row.id },
      {
        onSuccess: () => toast.showSuccess(`Soft-deleted ${row.name}.`),
        onError: (err) => toast.showError(err.message || "Could not delete content item."),
      },
    );
  };

  return (
    <Stack spacing={2} data-testid="content-items-table-root">
      {confirmDialog}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as ContentItemType | "");
            }}
            sx={{ minWidth: 160 }}
            SelectProps={{
              SelectDisplayProps: {
                "data-testid": "content-items-type-filter",
              } as Record<string, string>,
            }}
          >
            <MenuItem value="">All</MenuItem>
            {ALL_CONTENT_ITEM_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Block type"
            value={blockTypeFilter}
            onChange={(e) => {
              setBlockTypeFilter(e.target.value as BlockType | "");
            }}
            sx={{ minWidth: 200 }}
            SelectProps={{
              SelectDisplayProps: {
                "data-testid": "content-items-block-type-filter",
              } as Record<string, string>,
            }}
          >
            <MenuItem value="">All</MenuItem>
            {ALL_BLOCK_TYPES.map((bt) => (
              <MenuItem key={bt} value={bt}>
                {bt}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          data-testid="content-items-new-button"
        >
          New content item
        </Button>
      </Stack>

      {items.isError ? (
        <Alert severity="error" data-testid="content-items-error">
          {items.error.message}
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <TableContainer>
          <Table aria-label="Content items" data-testid="content-items-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Block type</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.isPending ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 6 }}
                    data-testid="content-items-empty"
                  >
                    <Typography variant="body2" color="text.secondary">
                      {typeFilter !== "" || blockTypeFilter !== ""
                        ? "No content items match the active filters."
                        : "No content items yet. Create one above, or generate them from the AI Studio."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    data-testid="content-items-row"
                    data-content-item-id={row.id}
                  >
                    <TableCell>
                      <Link
                        to="/sites/$siteId/content/$contentItemId"
                        params={{ siteId, contentItemId: row.id }}
                        style={{ textDecoration: "none", color: "inherit" }}
                        data-testid="content-items-row-name-link"
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row.name}
                        </Typography>
                      </Link>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily:
                            '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Monaco, monospace',
                        }}
                      >
                        {row.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.type} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {row.blockType ? (
                        <Chip size="small" label={row.blockType} variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(row.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        {/*
                         * Wrap the IconButton in a TanStack `<Link>`
                         * instead of using MUI's `component={Link}`
                         * prop: the typed-params API on the Link is
                         * incompatible with IconButton's prop
                         * inference, and the wrapper keeps the typed
                         * navigation while preserving the IconButton
                         * styling + accessibility attributes.
                         */}
                        <Link
                          to="/sites/$siteId/content/$contentItemId"
                          params={{ siteId, contentItemId: row.id }}
                          style={{ display: "inline-flex" }}
                        >
                          <IconButton
                            aria-label={`Edit ${row.name}`}
                            data-testid="content-items-row-edit-button"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Link>
                      </Tooltip>
                      <Tooltip title="Soft-delete">
                        <IconButton
                          aria-label={`Soft-delete ${row.name}`}
                          onClick={() => {
                            void handleDelete(row);
                          }}
                          disabled={remove.isPending}
                          data-testid="content-items-row-delete-button"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {items.data?.meta ? (
          <Box sx={{ borderTop: 1, borderColor: "divider" }}>
            <TablePagination
              component="div"
              count={items.data.meta.total}
              page={page - 1}
              onPageChange={(_e, next) => setPage(next + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              data-testid="content-items-pagination"
            />
          </Box>
        ) : null}
      </Paper>

      <CreateContentItemDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        siteId={siteId}
      />
    </Stack>
  );
}
